import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Check, ShieldCheck } from 'lucide-react';
import { getProductBySlug, products } from '@/data/products';

const pages = {
  '/services/no-account-vpn': {
    title: 'No Subscription VPN Passes | PhantomPathVPN',
    heading: 'VPN access without a monthly subscription',
    eyebrow: 'PAY AS YOU GO VPN',
    description: 'Buy VPN access for 1 hour, 24 hours, 14 days or 30 days without creating a PhantomPath account or starting a subscription.',
    intro: 'Only need a VPN for one day, a short trip or one private session? Choose a time-limited pass and pay only for the access period you need.',
    categories: ['vpn'],
    accent: '#00ffc8',
    primaryTarget: '/#pricing',
    primaryLabel: 'Choose your VPN pass',
    benefits: ['No recurring VPN payment', 'No traditional account or email required', 'Short-term access from one hour', 'RAM-only VPN routing with no browsing or VPN traffic logs'],
    questions: [
      ['Can I buy a VPN for just 24 hours?', 'Yes. PhantomPath offers a 24-Hour VPN Pass as a one-off purchase. It expires after the stated access period and does not become a monthly subscription.'],
      ['Can I use a VPN without a monthly subscription?', 'Yes. Choose a 1-hour, 24-hour, 14-day or 30-day VPN pass. There is no automatic renewal.'],
      ['Can I buy a VPN without creating an account?', 'Yes. You do not need to create a PhantomPath account to use a VPN pass. Your chosen payment provider may process the information required to complete the transaction.'],
      ['How do no-account VPN passes work?', 'Select a duration, complete payment and receive the access details for that pass. The access code exists for the duration of the session rather than becoming a recurring membership.'],
      ['Is this suitable as a temporary or holiday VPN?', 'The time-limited passes are designed for short-term needs such as holidays, shared networks and public Wi-Fi. Choose the duration that matches your trip.'],
    ],
    relatedLinks: [
      ['/compare/pay-as-you-go-vpn-vs-subscription', 'Pay as you go VPN versus subscription'],
      ['/learn/privacy-architecture', 'How the privacy architecture works'],
      ['/services/holiday-esim-data', 'VPN and eSIM options for holidays'],
    ],
  },
  '/services/travel-esim': {
    title: 'Short-Term Travel eSIM Data | PhantomPathVPN',
    heading: 'Travel eSIM data without a subscription',
    eyebrow: 'TRAVEL CONNECTIVITY',
    description: 'Buy short-term travel eSIM data for 7, 14 or 30 days, with optional VPN bundles and no recurring contract.',
    intro: 'Choose your destination, duration and data allowance before travelling. Compatible phones can use the travel eSIM alongside an existing SIM.',
    categories: ['travel', 'privacy'],
    accent: '#2dd4bf',
    primaryTarget: '/#travel-packs',
    primaryLabel: 'Choose travel eSIM data',
    benefits: ['7, 14 and 30-day options', 'One-off payment with no auto-renewal', 'Privacy Packs bundle travel eSIM data with VPN access', 'Keep a compatible primary SIM installed'],
    questions: [
      ['Can I buy short-term eSIM data for international travel?', 'Yes. PhantomPath offers travel eSIM packs for supported destinations with 7, 14 and 30-day durations.'],
      ['Do travel eSIMs require a contract or recurring payment?', 'No. Each pack is a one-off purchase for its stated data allowance and duration.'],
      ['Can I get an eSIM and VPN together for travel?', 'Yes. Privacy Packs combine the selected travel eSIM data allowance with PhantomPath VPN access for the same stated duration.'],
      ['Can I use a travel eSIM without removing my normal SIM?', 'Compatible devices can keep an existing SIM installed while using the travel eSIM for mobile data. Device support and setup options vary by manufacturer.'],
    ],
    relatedLinks: [
      ['/guides/avoid-roaming-charges-with-esim', 'How to reduce roaming charges with an eSIM'],
      ['/services/holiday-esim-data', 'Holiday eSIM data and VPN'],
      ['/services/business-travel-connectivity', 'Connectivity for business travel'],
    ],
  },
  '/services/virtual-numbers': {
    title: 'Temporary UK & USA Virtual Numbers | PhantomPathVPN',
    heading: 'A temporary virtual number without another physical SIM',
    eyebrow: 'UK AND USA NUMBERS',
    description: 'Buy a dedicated UK or USA virtual number for 30 days with no recurring contract. Optional minutes and SMS are available where supported.',
    intro: 'Use a dedicated, time-limited UK or USA number alongside a compatible existing SIM or eSIM. Platform compatibility and message delivery depend on third parties and are not guaranteed.',
    categories: ['numbers'],
    accent: '#38bdf8',
    primaryTarget: '/#virtual-numbers',
    primaryLabel: 'Choose a virtual number',
    benefits: ['Dedicated UK or USA number for 30 days', 'No second physical SIM required', 'No recurring number subscription', 'Optional minutes and SMS where supported'],
    questions: [
      ['Can I get a temporary UK virtual number without a second SIM card?', 'Yes. The UK virtual-number package provides a dedicated number for 30 days and works alongside a compatible existing SIM or eSIM.'],
      ['Are PhantomPath virtual numbers pay as you go?', 'They are sold as one-off 30-day packages rather than recurring contracts. Optional minutes and SMS can be selected where shown.'],
      ['Can I buy a temporary USA virtual number?', 'Yes. A dedicated USA virtual number is available for 30 days. Supported USA numbers require the telecom-compliance verification shown during configuration.'],
      ['Will the number work with every verification platform?', 'No. Each third-party platform sets its own rules, so verification compatibility and SMS delivery cannot be guaranteed.'],
      ['Can the number call emergency services?', 'No. PhantomPath virtual numbers do not support emergency calling, including 999, 112 or 911.'],
    ],
    relatedLinks: [
      ['/services/business-virtual-number', 'Virtual numbers for business travel'],
      ['/services/business-travel-connectivity', 'Business travel connectivity'],
      ['/learn/temporary-identity-systems', 'How eSIM + VPN Packs work'],
    ],
  },
  '/learn/temporary-identity-systems': {
    title: 'Privacy Tools Without Subscriptions | PhantomPathVPN',
    heading: 'A pay-as-you-go privacy stack for travel and temporary needs',
    eyebrow: 'PRIVACY ON DEMAND',
    description: 'Learn how time-limited VPN access, travel eSIM data and optional UK or USA virtual numbers can work together without recurring subscriptions.',
    intro: 'PhantomPath separates the tools so you can choose only what you need: a VPN pass, travel data, a virtual number, or a Privacy Pack that combines eSIM data with VPN access.',
    categories: ['privacy'],
    accent: '#818cf8',
    primaryTarget: '/#privacy-packs',
    primaryLabel: 'Explore eSIM + VPN Packs',
    benefits: ['Time-limited services instead of recurring contracts', 'Travel eSIM data with VPN bundles available', 'Optional UK or USA virtual-number add-ons where shown', 'No mandatory traditional customer account'],
    questions: [
      ['What is a temporary digital privacy stack?', 'It is a group of time-limited tools selected for a specific need, such as VPN access, travel eSIM data and an optional virtual number, without keeping an ongoing monthly subscription.'],
      ['Can I combine a VPN and eSIM for travel?', 'Yes. PhantomPath Privacy Packs bundle a stated travel eSIM allowance with VPN access for 7, 14 or 30 days.'],
      ['Can I add a virtual number to a travel package?', 'An optional UK or USA virtual number can be selected on supported travel, privacy and unlimited packages where the add-on is displayed.'],
      ['Do I have to buy every service?', 'No. The products remain separate so you can choose a VPN pass, eSIM data, a virtual number or a bundle according to your actual requirement.'],
    ],
  },
  '/services/holiday-esim-data': {
    title: 'Holiday eSIM Data & Short-Term VPN | PhantomPathVPN',
    heading: 'Holiday eSIM data and VPN access without a subscription',
    eyebrow: 'CONNECTED ABROAD',
    description: 'Buy short-term holiday eSIM data and optional VPN access for travel without starting a recurring subscription.',
    intro: 'Choose prepaid travel data for your destination and trip length. Using the travel eSIM for mobile data can reduce reliance on your home network\'s data roaming, while a time-limited VPN pass supports safer browsing on hotel, airport and shared Wi-Fi.',
    productSlugs: ['travel-esim-5gb-1-week', 'travel-esim-10gb-2-week', 'travel-esim-20gb-30-day', 'privacy-pack-5gb-1-week', 'privacy-pack-10gb-2-week', 'vpn-2-week'],
    accent: '#2dd4bf',
    primaryTarget: '/#travel-packs',
    primaryLabel: 'Choose holiday eSIM data',
    benefits: ['Travel data for 7, 14 or 30 days', 'One-off purchase with no automatic renewal', 'VPN and eSIM bundles available', 'Useful for holidays, airports, hotels and shared networks'],
    questions: [
      ['What is the best eSIM option for a short holiday?', 'Choose a data allowance and duration that match your destination and expected use. PhantomPath offers supported-destination travel eSIM packs for 7, 14 and 30 days.'],
      ['Can a travel eSIM help me avoid roaming charges?', 'Using the travel eSIM for mobile data can help avoid your home provider\'s mobile-data roaming charges. Charges may still apply if your primary SIM uses calls, texts or data, so check your phone settings and provider terms.'],
      ['Can I buy an eSIM just for a one-week holiday?', 'Yes. Seven-day travel eSIM packages are available for supported destinations without a recurring subscription.'],
      ['Can I get holiday eSIM data and a VPN together?', 'Yes. Privacy Packs combine a stated travel eSIM allowance with PhantomPath VPN access for the selected duration.'],
      ['Can I buy a VPN only for my holiday?', 'Yes. Standalone VPN passes are available for 1 hour, 24 hours, 14 days or 30 days with no automatic renewal.'],
    ],
    relatedLinks: [
      ['/guides/avoid-roaming-charges-with-esim', 'How to reduce roaming charges with an eSIM'],
      ['/services/travel-esim', 'Short-term travel eSIM data'],
      ['/compare/pay-as-you-go-vpn-vs-subscription', 'Compare VPN payment models'],
    ],
  },
  '/services/business-travel-connectivity': {
    title: 'Business Travel eSIM, VPN & Virtual Numbers | PhantomPathVPN',
    heading: 'Flexible connectivity for business travellers',
    eyebrow: 'WORK WHILE TRAVELLING',
    description: 'One-off travel eSIM data, short-term VPN access and optional UK or USA virtual numbers for business trips without recurring subscriptions.',
    intro: 'Build a temporary travel setup for a work trip: mobile data for a supported destination, time-limited VPN access for shared networks and, where suitable, a dedicated UK or USA virtual number alongside your existing SIM.',
    productSlugs: ['privacy-pack-20gb-30-day', 'privacy-pack-50gb-30-day', 'unlimited-data-vpn-30-day', 'vpn-30-day', 'uk-virtual-number-30-day', 'usa-virtual-number-30-day'],
    accent: '#818cf8',
    primaryTarget: '/#privacy-packs',
    primaryLabel: 'Explore business travel options',
    benefits: ['Travel eSIM data without a recurring contract', 'Short-term VPN access for public and shared networks', 'Dedicated UK or USA virtual numbers for 30 days', 'Choose separate services or a data and VPN bundle'],
    questions: [
      ['What connectivity do I need for an international business trip?', 'A compatible travel eSIM can provide mobile data at your destination, while a short-term VPN pass can support safer use of hotel, airport and shared networks. A virtual number is optional where a separate UK or USA number is useful.'],
      ['Can business travellers buy eSIM data without a subscription?', 'Yes. PhantomPath travel eSIM and Privacy Pack options are one-off purchases for their stated allowance and duration.'],
      ['Can I combine business travel eSIM data with VPN access?', 'Yes. Privacy Packs and the Unlimited Data + VPN package combine travel data with PhantomPath VPN access.'],
      ['Can I use a separate virtual number while travelling for business?', 'A dedicated UK or USA virtual number can work alongside a compatible existing SIM or eSIM for 30 days. Calling, messaging and third-party platform compatibility depend on the selected options and external providers.'],
      ['Can a business travel eSIM reduce roaming costs?', 'Using the travel eSIM for mobile data can reduce reliance on your home network\'s data roaming. Your primary SIM may still generate charges if it uses calls, texts or data, so configure the device carefully and check provider terms.'],
    ],
    relatedLinks: [
      ['/guides/avoid-roaming-charges-with-esim', 'How to reduce roaming charges with an eSIM'],
      ['/services/business-virtual-number', 'Temporary numbers for business travel'],
      ['/services/travel-esim', 'Short-term travel eSIM data'],
    ],
  },
  '/guides/avoid-roaming-charges-with-esim': {
    title: 'Reduce Roaming Charges With a Travel eSIM | PhantomPathVPN',
    heading: 'How to use a travel eSIM to reduce roaming charges',
    eyebrow: 'TRAVEL ESIM GUIDE',
    description: 'A practical guide to using travel eSIM data abroad while reducing reliance on home-network data roaming and avoiding common phone-setting mistakes.',
    intro: 'A travel eSIM can provide mobile data at a supported destination without routing that data through your home mobile plan. The important part is choosing the correct data line and checking your primary SIM settings before you travel.',
    productSlugs: ['travel-esim-5gb-1-week', 'travel-esim-10gb-2-week', 'travel-esim-20gb-30-day', 'privacy-pack-5gb-1-week', 'privacy-pack-10gb-2-week'],
    accent: '#2dd4bf',
    primaryTarget: '/#travel-packs',
    primaryLabel: 'Choose travel eSIM data',
    benefits: ['One-off data for supported destinations', '7, 14 and 30-day options', 'Keep a compatible primary SIM installed', 'VPN and eSIM bundles are available'],
    guideSteps: [
      ['Check compatibility before buying', 'Confirm that the phone supports eSIM, is network-unlocked and supports the destination you need.'],
      ['Install the travel eSIM', 'Generate the eSIM in the PhantomPath Portal, then add it from the QR code or the manual activation details shown on the dashboard.'],
      ['Select it for mobile data', 'In your phone settings, choose the travel eSIM as the line used for mobile or cellular data.'],
      ['Protect the primary SIM from data roaming', 'Turn data roaming off for the primary SIM and disable automatic cellular data switching so the phone does not silently move data back to the home network.'],
      ['Enable the travel data line', 'Turn the travel eSIM on and follow the activation instructions. Some travel eSIMs require data roaming to be enabled on the travel line itself.'],
      ['Check before heavy use', 'Confirm which line is carrying mobile data and review both providers\' terms. Calls and texts on the primary SIM may still create charges.'],
    ],
    contentSections: [
      {
        heading: 'What a travel eSIM can and cannot prevent',
        body: ['Using the travel eSIM for mobile data can reduce or avoid your home provider\'s data-roaming charges. It does not automatically block charges from calls, texts, voicemail, premium services or accidental use by the primary SIM.'],
        bullets: ['Keep the primary line\'s data roaming switched off', 'Disable automatic mobile-data switching', 'Check whether incoming calls or voicemail create charges', 'Monitor the active data line after landing'],
      },
    ],
    questions: [
      ['Can an eSIM help me avoid roaming charges?', 'It can help you avoid your home provider\'s mobile-data roaming charges when the travel eSIM is selected for data. Your primary SIM may still create charges for calls, texts, voicemail or accidental data use.'],
      ['Should data roaming be on or off when using a travel eSIM?', 'Turn data roaming off on the primary SIM. Some travel eSIMs require data roaming to be enabled on the travel line, so follow the activation instructions for the package you purchased.'],
      ['Can I keep my normal SIM active while using a travel eSIM?', 'Yes, on compatible devices. Keep the travel eSIM selected for mobile data and review call and text charges that may apply to the primary SIM.'],
      ['When should I install a travel eSIM?', 'Install it while you have reliable internet access before or during your trip, but follow the package instructions so the validity period is not started earlier than intended.'],
      ['Can I add a short-term VPN to my travel data?', 'Yes. Privacy Packs combine a stated eSIM data allowance with VPN access, or you can choose a separate time-limited VPN pass.'],
    ],
    relatedLinks: [
      ['/services/holiday-esim-data', 'Holiday eSIM data and VPN'],
      ['/services/business-travel-connectivity', 'Connectivity for business travel'],
      ['/how-it-works', 'Full activation instructions'],
    ],
  },
  '/compare/pay-as-you-go-vpn-vs-subscription': {
    title: 'Pay As You Go VPN vs Monthly Subscription | PhantomPathVPN',
    heading: 'Pay as you go VPN versus a monthly subscription',
    eyebrow: 'CHOOSE THE RIGHT VPN MODEL',
    description: 'Compare one-off VPN passes with monthly VPN subscriptions, including duration, commitment, accounts and the situations each model suits.',
    intro: 'The right VPN payment model depends on how often you need it. A time-limited pass can suit a holiday, one day of access or occasional public Wi-Fi, while frequent continuous use may suit a longer-term service.',
    productSlugs: ['vpn-1-hour', 'vpn-24-hour', 'vpn-2-week', 'vpn-30-day'],
    accent: '#00ffc8',
    primaryTarget: '/#pricing',
    primaryLabel: 'Choose your VPN pass',
    benefits: ['1 hour, 24 hour, 14 day and 30 day passes', 'No automatic renewal', 'No PhantomPath account required', 'The access period and price are shown before payment'],
    comparison: {
      headings: ['Question', 'PhantomPath VPN pass', 'Typical monthly subscription'],
      rows: [
        ['How are you charged?', 'One-off price for the selected duration', 'Recurring charge until cancelled'],
        ['How long is access?', '1 hour, 24 hours, 14 days or 30 days', 'Usually a monthly or longer billing period'],
        ['Is an account required?', 'No PhantomPath account required', 'Often requires an account and recurring billing relationship'],
        ['Who may prefer it?', 'Occasional users, short trips and temporary access', 'People who need continuous, frequent VPN use'],
        ['Does it renew?', 'No automatic renewal', 'Usually renews under the provider\'s plan terms'],
      ],
    },
    contentSections: [
      {
        heading: 'When a time-limited VPN pass makes sense',
        body: ['Choose a pass when you know the period you need and do not want an ongoing membership. The shortest PhantomPath option is one hour; the longest standalone VPN pass is 30 days.'],
        bullets: ['A single day on public or shared Wi-Fi', 'A two-week holiday or work trip', 'Temporary access without an ongoing account', 'Testing whether a time-limited service fits your needs'],
      },
      {
        heading: 'When a subscription may make more sense',
        body: ['If you need a VPN continuously for many months, compare the total cost and convenience of repeated passes with a reputable longer-term service. PhantomPath is designed around privacy on demand rather than pretending one model fits everybody.'],
      },
    ],
    questions: [
      ['Can I buy a VPN for just one day?', 'Yes. The 24-Hour VPN Pass is a one-off purchase and does not renew as a monthly subscription.'],
      ['Is pay as you go VPN the same as a free VPN?', 'No. A pay-as-you-go service charges a stated one-off price for a defined access period. It is not presented as a free service.'],
      ['Do I need a PhantomPath account to buy a VPN pass?', 'No. You do not need to create a PhantomPath account to use a VPN pass. Your chosen payment provider may process the information required to complete the transaction.'],
      ['Does a VPN pass automatically renew?', 'No. PhantomPath VPN passes end after their stated access period and do not automatically renew.'],
      ['Which VPN duration should I choose?', 'Match the pass to the period you actually need: one hour, 24 hours, 14 days or 30 days. For continuous long-term use, compare your total requirements before buying repeated passes.'],
    ],
    relatedLinks: [
      ['/services/no-account-vpn', 'VPN access without an account'],
      ['/learn/privacy-architecture', 'How the privacy architecture works'],
      ['/zero-log-manifesto', 'Read the Zero-Log Manifesto'],
    ],
  },
  '/services/business-virtual-number': {
    title: 'Temporary Business Virtual Number for Travel | UK & USA',
    heading: 'A temporary UK or USA number for business travel',
    eyebrow: 'SECOND NUMBER WITHOUT A SECOND SIM',
    description: 'Buy a dedicated UK or USA virtual number for 30 days for general business communication while travelling, without another physical SIM or recurring contract.',
    intro: 'A separate virtual number can help keep general work calls and messages apart from a personal number during a trip. PhantomPath numbers use an internet connection and work alongside a compatible existing SIM or eSIM.',
    productSlugs: ['uk-virtual-number-30-day', 'usa-virtual-number-30-day'],
    accent: '#38bdf8',
    primaryTarget: '/#virtual-numbers',
    primaryLabel: 'Choose a UK or USA number',
    benefits: ['Dedicated UK or USA number for 30 days', 'No second physical SIM required', 'One-off package with no recurring contract', 'Optional minutes and SMS where supported'],
    contentSections: [
      {
        heading: 'Useful for clear work and personal separation',
        body: ['Use a dedicated number for supported general calls and messaging without publishing your personal number for every short-term work contact. The service is time-limited and does not replace a full business phone system.'],
        bullets: ['Short business trips', 'Temporary client or supplier contact', 'A separate number alongside an existing SIM', 'General calls and messaging where supported'],
      },
      {
        heading: 'Important limitations before you buy',
        body: ['Virtual numbers depend on third-party telecommunications networks. SMS delivery and compatibility with verification platforms are not guaranteed. PhantomPath virtual numbers do not support emergency calling, including 999, 112 or 911.'],
      },
    ],
    questions: [
      ['Can I use a virtual number as a second business number?', 'A PhantomPath UK or USA virtual number can provide a separate 30-day number for supported general calls and messaging. It is not a permanent business telephone system.'],
      ['Do I need another physical SIM?', 'No. The virtual number works over an internet connection alongside a compatible existing SIM or eSIM.'],
      ['Can I use the number while travelling?', 'Yes, where you have a suitable internet connection and the selected calling or messaging option is supported. Data charges may apply.'],
      ['Is this a disposable phone number?', 'PhantomPath provides a dedicated, time-limited 30-day virtual number. We describe it as temporary rather than disposable because availability, identity checks and telecom obligations still apply.'],
      ['Will it receive every verification code?', 'No. Platforms decide which numbers they accept, and SMS delivery or verification compatibility cannot be guaranteed.'],
      ['Can I call emergency services from it?', 'No. PhantomPath virtual numbers do not support emergency calling, including 999, 112 or 911. Maintain another way to contact emergency services.'],
    ],
    relatedLinks: [
      ['/services/virtual-numbers', 'Temporary UK and USA virtual numbers'],
      ['/services/business-travel-connectivity', 'Business travel connectivity'],
      ['/services/travel-esim', 'Short-term travel eSIM data'],
    ],
  },
};

const IntentLandingPage = () => {
  const { pathname } = useLocation();
  const page = pages[pathname];
  const matchingProducts = page.productSlugs
    ? page.productSlugs.map((slug) => getProductBySlug(slug)).filter(Boolean)
    : products.filter((product) => page.categories.includes(product.category));
  const canonical = `https://phantompathvpn.com${pathname}`;
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: page.heading,
        description: page.description,
        url: canonical,
        isPartOf: { '@type': 'WebSite', name: 'PhantomPathVPN', url: 'https://phantompathvpn.com/' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: page.questions.map(([question, answer]) => ({
          '@type': 'Question', name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer },
        })),
      },
      {
        '@type': 'ItemList',
        itemListElement: matchingProducts.map((product, index) => ({
          '@type': 'ListItem', position: index + 1, name: product.name,
          url: `https://phantompathvpn.com/services/${product.slug}`,
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://phantompathvpn.com/' },
          { '@type': 'ListItem', position: 2, name: page.eyebrow, item: canonical },
        ],
      },
    ],
  };

  return (
    <main className="min-h-screen bg-[#050505] px-4 pb-20 pt-28 text-white">
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="mx-auto max-w-5xl">
        <section className="border-b border-white/10 pb-10 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.24em]" style={{ color: page.accent }}>{page.eyebrow}</p>
          <h1 className="mx-auto mt-4 max-w-4xl text-3xl font-extrabold leading-tight sm:text-5xl">{page.heading}</h1>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-7 text-gray-300 sm:text-lg">{page.intro}</p>
          <Link to={page.primaryTarget} className="mt-7 inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-bold text-[#03110d] transition hover:brightness-110" style={{ backgroundColor: page.accent }}>
            {page.primaryLabel}<ArrowRight className="h-4 w-4" />
          </Link>
        </section>

        <section className="grid gap-3 py-10 sm:grid-cols-2" aria-label="Key benefits">
          {page.benefits.map((benefit) => (
            <div key={benefit} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.035] p-4 text-sm leading-6 text-gray-200">
              <Check className="mt-1 h-4 w-4 shrink-0" style={{ color: page.accent }} />{benefit}
            </div>
          ))}
        </section>

        {page.guideSteps && (
          <section className="pb-12" aria-labelledby="step-by-step-guide">
            <h2 id="step-by-step-guide" className="text-2xl font-bold">Step by step</h2>
            <ol className="mt-5 grid gap-4 md:grid-cols-2">
              {page.guideSteps.map(([title, copy], index) => (
                <li key={title} className="rounded-2xl border border-white/10 bg-white/[0.035] p-5">
                  <div className="flex items-start gap-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-extrabold text-[#03110d]" style={{ backgroundColor: page.accent }}>{index + 1}</span>
                    <div><h2 className="font-bold text-white">{title}</h2><p className="mt-2 text-sm leading-7 text-gray-300">{copy}</p></div>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {page.comparison && (
          <section className="pb-12" aria-labelledby="comparison-table">
            <h2 id="comparison-table" className="text-2xl font-bold">Side by side comparison</h2>
            <div className="mt-5 overflow-x-auto rounded-2xl border border-white/10">
              <table className="w-full min-w-[680px] border-collapse text-left text-sm">
                <thead className="bg-white/[0.07]"><tr>{page.comparison.headings.map((heading) => <th key={heading} className="px-5 py-4 font-bold text-white">{heading}</th>)}</tr></thead>
                <tbody className="divide-y divide-white/10">
                  {page.comparison.rows.map((row) => <tr key={row[0]}>{row.map((cell, index) => <td key={cell} className={`px-5 py-4 leading-6 ${index === 0 ? 'font-semibold text-white' : 'text-gray-300'}`}>{cell}</td>)}</tr>)}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {page.contentSections?.map((section) => (
          <section key={section.heading} className="mx-auto mb-10 max-w-4xl border-l-2 pl-5 sm:pl-7" style={{ borderColor: page.accent }}>
            <h2 className="text-xl font-bold text-white">{section.heading}</h2>
            {section.body.map((paragraph) => <p key={paragraph} className="mt-3 leading-7 text-gray-300">{paragraph}</p>)}
            {section.bullets && <ul className="mt-4 grid gap-2 sm:grid-cols-2">{section.bullets.map((bullet) => <li key={bullet} className="flex items-start gap-2 text-sm leading-6 text-gray-200"><Check className="mt-1 h-4 w-4 shrink-0" style={{ color: page.accent }} />{bullet}</li>)}</ul>}
          </section>
        ))}

        <section aria-labelledby="available-options">
          <h2 id="available-options" className="text-2xl font-bold">Available one-off options</h2>
          <p className="mt-2 text-sm text-gray-400">Select a package to see its current price, duration and included features.</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {matchingProducts.map((product) => (
              <Link key={product.slug} to={`/services/${product.slug}`} className="group rounded-xl border border-white/10 bg-white/[0.035] p-5 transition hover:border-white/25">
                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: product.accent }}>{product.categoryLabel}</p>
                <h3 className="mt-2 font-bold leading-6">{product.name}</h3>
                <div className="mt-4 flex items-center justify-between"><strong className="text-xl">{product.price}</strong><ArrowRight className="h-4 w-4 text-gray-500 transition group-hover:translate-x-1 group-hover:text-white" /></div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-14 max-w-4xl" aria-labelledby="questions">
          <div className="flex items-center gap-3"><ShieldCheck className="h-6 w-6" style={{ color: page.accent }} /><h2 id="questions" className="text-2xl font-bold">Questions people ask before buying</h2></div>
          <div className="mt-5 divide-y divide-white/10 border-y border-white/10">
            {page.questions.map(([question, answer]) => (
              <article key={question} className="py-6">
                <h2 className="text-lg font-bold text-white">{question}</h2>
                <p className="mt-2 leading-7 text-gray-300">{answer}</p>
              </article>
            ))}
          </div>
        </section>

        {page.relatedLinks && (
          <nav className="mx-auto mt-12 max-w-4xl border-t border-white/10 pt-8" aria-label="Related guides">
            <h2 className="text-lg font-bold text-white">Related guides</h2>
            <div className="mt-4 flex flex-wrap gap-3">
              {page.relatedLinks.map(([target, label]) => <Link key={target} to={target} className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.035] px-4 py-3 text-sm font-semibold text-gray-200 transition hover:border-white/25 hover:text-white">{label}<ArrowRight className="h-4 w-4" /></Link>)}
            </div>
          </nav>
        )}
      </div>
    </main>
  );
};

export default IntentLandingPage;
